import { useLabelVersion } from '../../../../../features/labels/LabelsProvider';
import { labelId, labelRegion, labelArea, labelCentro } from '../../../../../features/labels/labels';
import React from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Modal,
  Slider,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import TuneIcon from '@mui/icons-material/Tune';

import {
  get_feeder_score_area,
  get_feeder_score_center,
  get_feeder_score_region,
} from '../../../utils/api';
import {
  attentionCellBg,
  attentionTextColor,
  DIMENSION_META,
  dividerColor,
  formatNumber,
  isMaxScore,
  scoreCellBg,
  scoreTextColor,
  shortKey,
  translateOrFallback,
} from './feederScoreShared';
import {
  RECALC_STORAGE_KEY,
  applyRecalcToScoreData,
  defaultRecalcParams,
} from './recomputeFeederScore';
import {
  attentionPctForCell,
  buildHeaderSummary,
  buildRangeRanking,
  buildView,
} from './feederScoreObservabilityUtils';
import { buildFeederScoreSheets } from './export/buildFeederScoreSheets';
import { buildXlsx, downloadXlsx, sanitizeFileName } from '../../../utils/xlsx/miniXlsx';

const LEVEL_LABEL_KEYS = {
  centro: ['fs_cage', 'Jaula'],
  area: ['center_label', 'Centro'],
  region: ['area', 'Área'],
};

const CloudFeederScorePanel = ({
  mode = 'score',
  translate,
  servidor,
  dias,
  groupMode,
  nivelSeleccionado,
  regionSeleccionada,
  areaSeleccionada,
  centroSeleccionado,
  registerXlsxExport,
  clearXlsxExport,
}) => {
  useLabelVersion();
  const t = React.useCallback((key, fallback) => translateOrFallback(translate, key, fallback), [translate]);
  const [scoreData, setScoreData] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [selectedCell, setSelectedCell] = React.useState(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [rankingBy, setRankingBy] = React.useState(mode === 'attention' ? 'attention' : 'score');
  const [recalcOpen, setRecalcOpen] = React.useState(false);
  const [recalcParams, setRecalcParams] = React.useState(null);
  const [exporting, setExporting] = React.useState(false);

  React.useEffect(() => {
    if (mode === 'attention') setRankingBy('attention');
  }, [mode]);

  React.useEffect(() => {
    const fetchScores = async () => {
      const startDate = dias?.startDate;
      const dayRange = dias?.dayRange;
      if (!servidor || !startDate || !dayRange || !regionSeleccionada) return;
      if (nivelSeleccionado === 'centro' && (!areaSeleccionada || !centroSeleccionado)) return;
      if (nivelSeleccionado === 'area' && !areaSeleccionada) return;

      setLoading(true);
      setError('');
      try {
        let payload = {};
        if (nivelSeleccionado === 'centro') {
          payload = await get_feeder_score_center(regionSeleccionada, areaSeleccionada, centroSeleccionado, startDate, dayRange, servidor);
        } else if (nivelSeleccionado === 'area') {
          payload = await get_feeder_score_area(regionSeleccionada, areaSeleccionada, startDate, dayRange, servidor);
        } else if (nivelSeleccionado === 'region') {
          payload = await get_feeder_score_region(
            regionSeleccionada,
            startDate,
            dayRange,
            servidor,
            recalcParams?.mode === 'standard' && recalcParams?.exact ? { granularity: 'center' } : {},
          );
        }
        setScoreData(payload || {});
        setSelectedCell(null);
      } catch (err) {
        console.error('[FeederScore] Error al cargar scores', { nivelSeleccionado, regionSeleccionada, areaSeleccionada, centroSeleccionado, err });
        setError(err?.message || t('feeder_score_load_error', 'No se pudo cargar FeederScore'));
        setScoreData({});
      } finally {
        setLoading(false);
      }
    };

    fetchScores();
  }, [areaSeleccionada, centroSeleccionado, dias, nivelSeleccionado, recalcParams?.exact, recalcParams?.mode, regionSeleccionada, servidor, t]);

  React.useEffect(() => {
    try {
      const stored = window.localStorage.getItem(RECALC_STORAGE_KEY);
      if (stored) setRecalcParams(JSON.parse(stored));
    } catch (_error) {}
  }, []);

  const exactRegion = nivelSeleccionado === 'region' && recalcParams?.mode === 'standard' && recalcParams?.exact;
  const displayedScoreData = React.useMemo(
    () => applyRecalcToScoreData(scoreData, recalcParams, { groupRegionCenters: exactRegion }),
    [exactRegion, scoreData, recalcParams],
  );
  const view = React.useMemo(() => buildView(displayedScoreData, groupMode || 'day', recalcParams), [displayedScoreData, groupMode, recalcParams]);
  const columns = view.columns;
  const rows = view.rows;
  const headerSummary = React.useMemo(() => buildHeaderSummary(columns, rows, view.data), [columns, rows, view.data]);
  const ranking = React.useMemo(() => buildRangeRanking(columns, rows, view.data, rankingBy), [columns, rows, rankingBy, view.data]);
  const selectedData = selectedCell ? view.data?.[selectedCell.day]?.[selectedCell.rowId] : null;
  const rowLabelInfo = LEVEL_LABEL_KEYS[nivelSeleccionado] || LEVEL_LABEL_KEYS.centro;
  const rowLabel = t(rowLabelInfo[0], rowLabelInfo[1]);
  const title = mode === 'attention'
    ? t('alarm_attention_menu', 'Atención a Alarmas')
    : t('feeder_score_menu', 'Puntaje de alimentación');

  const rowName = React.useCallback((rowId) => {
    if (rowId === '__CENTER__') return t('fs_center', 'CENTRO');
    return labelId(rowId, nivelSeleccionado === 'region' ? 'ra' : 'rac', view.nameById[rowId] || rowId.split('-').slice(-1)[0] || rowId);
  }, [t, view.nameById]);

  const toggleSelect = (day, rowId) => {
    setSelectedCell((prev) => (prev && prev.day === day && prev.rowId === rowId ? null : { day, rowId }));
  };

  const isTabScore = mode !== 'attention' ;

  const exportXlsx = React.useCallback(async () => {
    setExporting(true);
    try {
      const sheets = buildFeederScoreSheets({
        mode,
        view,
        nivelSeleccionado,
        groupMode: groupMode || 'day',
        recalcParams,
        translate,
        metadata: {
          region: regionSeleccionada,
          area: areaSeleccionada,
          center: centroSeleccionado,
          dias,
        },
      });
      const blob = await buildXlsx(sheets);
      const entity = nivelSeleccionado === 'region'
        ? regionSeleccionada
        : nivelSeleccionado === 'area' ? areaSeleccionada : centroSeleccionado;
      const displayEntity = nivelSeleccionado === 'region' ? labelRegion(regionSeleccionada, entity) : nivelSeleccionado === 'area' ? labelArea({ region: regionSeleccionada, area: areaSeleccionada }, entity) : labelCentro({ region: regionSeleccionada, area: areaSeleccionada, centro: centroSeleccionado }, entity);
      const date = (value) => String(value || '').replaceAll('/', '-');
      const menu = mode === 'attention' ? 'AtencionAlarmas' : 'FeederScore';
      downloadXlsx(
        sanitizeFileName(`${menu}_${displayEntity || nivelSeleccionado}_${date(dias?.startDate)}_${date(dias?.endDate)}.xlsx`),
        blob,
      );
    } catch (exportError) {
      console.error('[SCAPP XLSX] No se pudo exportar Feeder Score', exportError);
      setError(t('export_error', 'No se pudo generar el archivo Excel.'));
    } finally {
      setExporting(false);
    }
  }, [
    areaSeleccionada,
    centroSeleccionado,
    dias,
    groupMode,
    mode,
    nivelSeleccionado,
    recalcParams,
    regionSeleccionada,
    t,
    translate,
    view,
  ]);

  React.useEffect(() => {
    if (typeof registerXlsxExport !== 'function') return undefined;
    const id = `feeder-score-${mode}`;
    registerXlsxExport({
      id,
      run: exportXlsx,
      disabled: loading || exporting || !rows.length,
      loading: exporting,
      hasOptions: false,
    });
    return () => clearXlsxExport?.(id);
  }, [
    clearXlsxExport,
    exportXlsx,
    exporting,
    loading,
    mode,
    registerXlsxExport,
    rows.length,
  ]);

  return (
    <Box sx={{ width: '100%', height: '100%', p: '1vh 1vw', color: '#f3f5f8', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1vw', mb: '1.2vh' }}>
        <Box>
          <Typography sx={{ fontSize: '1.35vw', fontWeight: 800, lineHeight: 1.1 }}>{title}</Typography>
          <Typography sx={{ fontSize: '.72vw', opacity: .64 }}>
            {t('temporal_range', 'Rango temporal')}: {dias?.startDate} - {dias?.endDate} · {t(`fs_gran_${groupMode || 'day'}`, groupMode || 'day')}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        {isTabScore && (
          <Button size="small" startIcon={<TuneIcon />} variant={recalcParams?.mode === 'standard' ? 'contained' : 'outlined'} sx={scoreButtonSx} onClick={() => setRecalcOpen(true)}>
            {t('fs_recalculate', 'Recalcular')}
          </Button>
        )}
        {loading && <CircularProgress size={18} thickness={5} sx={{ color: '#e84e0e' }} />}
      </Box>

      {error && (
        <Box sx={{ mb: '1vh', color: '#ff9f7e', fontSize: '.78vw' }}>{error}</Box>
      )}

      <HeaderSummary mode={mode} summary={headerSummary} rowsLabel={rowLabel} t={t} />

      <Grid container spacing={2} alignItems="flex-start" sx={{ height: 'calc(100% - 20vh)' }}>
        <Grid item xs={8.4} sx={{ height: '100%' }}>
          <TableContainer component={Paper} sx={tableContainerSx}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...cellHeadSx, ...stickyColSx }}>{rowLabel}</TableCell>
                  {columns.map((col) => (
                    <TableCell key={col.key} align="center" sx={cellHeadSx}>{col.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((rowId) => {
                  const rowSelected = selectedCell?.rowId === rowId;
                  return (
                    <TableRow
                      key={rowId}
                      sx={{ ...rowSx, ...(rowSelected ? { background: 'rgba(232,78,14,.08)' } : {}) }}
                    >
                      <TableCell sx={nameCellSx}>{rowName(rowId)}</TableCell>
                      {columns.map((col) => {
                        const value = view.data?.[col.key]?.[rowId];
                        return (
                          <TableCell key={`${rowId}-${col.key}`} align="center" sx={cellSx}>
                            <MetricCell
                              mode={mode}
                              value={value}
                              selected={selectedCell?.day === col.key && selectedCell?.rowId === rowId}
                              onClick={() => toggleSelect(col.key, rowId)}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {!loading && rows.length === 0 && (
              <Box sx={{ py: '8vh', textAlign: 'center', color: 'rgba(255,255,255,.55)', fontSize: '.9vw' }}>
                {t('no_data', 'No hay datos')}
              </Box>
            )}
          </TableContainer>
        </Grid>

        <Grid item xs={3.6} sx={{ height: '100%' }}>
          <Box sx={sidePanelSx}>
            {mode === 'attention' && (
              <ButtonGroup size="small" sx={{ mb: '1vh', ...scoreButtonGroupSx }}>
                <Button variant={rankingBy === 'attention' ? 'contained' : 'outlined'} onClick={() => setRankingBy('attention')}>
                  {t('alarm_attention_ranking_attention', '% atención')}
                </Button>
                <Button variant={rankingBy === 'reaction' ? 'contained' : 'outlined'} onClick={() => setRankingBy('reaction')}>
                  {t('alarm_attention_ranking_reaction', 'Reacción')}
                </Button>
              </ButtonGroup>
            )}
            <Typography sx={{ fontSize: '.95vw', fontWeight: 800, mb: '.8vh' }}>{t('fs_ranking_title', 'Ranking')}</Typography>
            <Box sx={{ 
                maxHeight: isTabScore ? '25vh' : '38vh', 
                overflow: 'auto', 
                pr: '.25vw' 
              }}>
              {ranking.map(({ rowId, value, selectCol }, index) => (
                <RankingRow
                  key={rowId}
                  index={index}
                  mode={mode}
                  rankingBy={rankingBy}
                  label={value.name || rowName(rowId)}
                  value={value}
                  selected={selectedCell?.rowId === rowId && selectedCell?.day === selectCol}
                  onClick={() => selectCol && toggleSelect(selectCol, rowId)}
                />
              ))}
            </Box>
            {isTabScore ? (
              <Box sx={{ borderTop: `1px solid ${dividerColor}`, mt: '1.2vh', pt: '1.2vh' }}>
                <DetailPanel data={selectedData} mode={mode} t={t} onOpenDetail={() => setDetailOpen(true)} isCenterLevel={nivelSeleccionado === 'centro'} />
              </Box>
              ):
                (<Box sx={{ borderTop: `1px solid ${dividerColor}`, mt: '1.2vh', pt: '1.2vh' }} />
            )}
          </Box>
        </Grid>
      </Grid>

      <ScoreDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        data={selectedData}
        day={columns.find((col) => col.key === selectedCell?.day)?.label}
        t={t}
      />
      <RecalcModal
        open={recalcOpen}
        onClose={() => setRecalcOpen(false)}
        activeParams={recalcParams}
        defaults={defaultRecalcParams()}
        level={nivelSeleccionado}
        groupMode={groupMode || 'day'}
        t={t}
        onApply={(params) => {
          setRecalcParams(params);
          try { window.localStorage.setItem(RECALC_STORAGE_KEY, JSON.stringify(params)); } catch (_error) {}
          setRecalcOpen(false);
        }}
        onOriginal={() => {
          setRecalcParams(null);
          setRecalcOpen(false);
        }}
        onReset={() => {
          try { window.localStorage.removeItem(RECALC_STORAGE_KEY); } catch (_error) {}
        }}
      />
    </Box>
  );
};

const HeaderSummary = ({ mode, summary, rowsLabel, t }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: mode === 'attention' ? 'repeat(3, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: '.8vw', mb: '1.4vh' }}>
    <HeaderMetric
      title={`${summary.periods} ${t('fs_header_periods', 'períodos analizados')}`}
      lines={[
        `${rowsLabel}: ${summary.scoredRows}`,
        `${t('fs_header_total_scores', 'Puntuaciones totales')}: ${summary.totalScores}`,
      ]}
    />
    <HeaderMetric
      label={t('fs_header_attended_alarms', 'Alarmas atendidas')}
      value={summary.alarmTotal ? `${formatNumber(summary.alarmAttendedPct)}%` : '-'}
      valueColor={attentionTextColor(summary.alarmAttendedPct)}
      lines={[
        `${t('fs_header_attended', 'Atendidas')}: ${summary.alarmAttended}`,
        `${t('fs_header_total_alarms', 'Totales')}: ${summary.alarmTotal}`,
      ]}
    />
    <HeaderMetric
      label={t('fs_header_reaction', 'Reacción a alarmas')}
      value={summary.reactionMean == null ? '-' : formatSeconds(summary.reactionMean)}
      lines={[`${t('fs_header_reaction_p95', 'p95')}: ${summary.reactionP95 == null ? '-' : formatSeconds(summary.reactionP95)}`]}
    />
    {mode !== 'attention' && (
      <HeaderMetric
        label={t('fs_header_score', 'Score')}
        value={summary.scoreWeighted == null ? '-' : Number(summary.scoreWeighted).toFixed(1)}
        valueColor={scoreTextColor(summary.scoreWeighted)}
        lines={[
          `${t('fs_header_score_range', 'Rango')}: ${formatScoreRange(summary.scoreMin, summary.scoreMax)}`,
          `${t('fs_header_score_flat_avg', 'Promedio plano')}: ${summary.scoreMean == null ? '-' : Number(summary.scoreMean).toFixed(1)}`,
        ]}
      />
    )}
  </Box>
);

const HeaderMetric = ({ title, label, value, valueColor, lines }) => (
  <Box sx={headerMetricSx}>
    {label && <Typography sx={metricLabelSx}>{label}</Typography>}
    <Typography sx={{ fontSize: value ? '1.55vw' : '.95vw', fontWeight: 850, lineHeight: 1.05, color: valueColor || '#f3f5f8', fontVariantNumeric: 'tabular-nums' }}>
      {value || title}
    </Typography>
    <Box sx={{ display: 'grid', gap: '.15vh', mt: '.55vh' }}>
      {(lines || []).map((line) => (
        <Typography key={line} sx={{ fontSize: '.68vw', opacity: .68, fontVariantNumeric: 'tabular-nums' }}>{line}</Typography>
      ))}
    </Box>
  </Box>
);

export const RecalcModal = ({ open, onClose, activeParams, defaults, level, groupMode = 'day', t, onApply, onOriginal, onReset }) => {
  const [draft, setDraft] = React.useState(defaults);

  React.useEffect(() => {
    if (!open) return;
    const initial = activeParams || defaults;
    // En granularidad centro no se ofrece el selector de modo: se recalcula
    // siempre en 'estándar común' para que base y pesos apliquen de verdad.
    setDraft(level === 'centro' ? { ...initial, mode: 'standard' } : initial);
  }, [activeParams, defaults, open, level]);

  const setWeight = (key, value) => {
    setDraft((prev) => ({ ...prev, weights: { ...(prev.weights || {}), [key]: Number(value) } }));
  };
  const canExact = level === 'area' || level === 'region';
  const canAggregate = draft?.mode === 'standard' && (groupMode !== 'day' || (level === 'region' && Boolean(draft?.exact)));
  const showModeSelector = level !== 'centro';
  // En modo 'Según cada centro' los parámetros no se aplican: se bloquean para
  // que quede claro que no tienen efecto (evita el "no pasa nada" silencioso).
  const paramsDisabled = draft?.mode !== 'standard';

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '35vw',
        maxHeight: '92vh',
        bgcolor: '#101723',
        color: '#f3f5f8',
        border: `1px solid ${dividerColor}`,
        borderRadius: '.6rem',
        boxShadow: '0 18px 54px rgba(0,0,0,.55)',
        overflow: 'hidden',
      }}>
        <Box sx={{ px: '1.1vw', py: '1vh', borderBottom: `1px solid ${dividerColor}` }}>
          <Typography sx={{ fontSize: '.95vw', fontWeight: 850 }}>{t('fs_recalc_modal_title', 'Recalcular score')}</Typography>
          <Typography sx={{ fontSize: '.66vw', opacity: .62, mt: '.25vh' }}>{t('fs_recalc_exploratory', 'Temporal en este navegador; no se guarda en servidor.')}</Typography>
        </Box>
        <Box sx={{ px: '1.1vw', py: '1.1vh', maxHeight: '58vh', overflowY: 'auto', display: 'grid', gap: '1vh' }}>
          {showModeSelector && (
            <TextField select size="small" label={t('fs_recalc_mode', 'Modo')} value={draft?.mode || 'center'} onChange={(event) => setDraft((prev) => ({ ...prev, mode: event.target.value }))}>
              <MenuItem value="center">{t('fs_recalc_mode_center', 'Según cada centro')}</MenuItem>
              <MenuItem value="standard">{t('fs_recalc_mode_standard', 'Estándar común')}</MenuItem>
            </TextField>
          )}
          {paramsDisabled && (
            <Typography sx={{ fontSize: '.66vw', color: '#e0a93b', lineHeight: 1.4 }}>
              {t('fs_recalc_params_locked', 'Estás en “Según cada centro”: la base y los pesos no se aplican. Cambia a “Estándar común” para editarlos.')}
            </Typography>
          )}
          <TextField size="small" type="number" disabled={paramsDisabled} label={t('fs_recalc_base', 'Peso base')} value={draft?.base ?? 20} onChange={(event) => setDraft((prev) => ({ ...prev, base: Number(event.target.value) }))} />
          <Typography sx={{ fontSize: '.68vw', fontWeight: 800, color: '#e84e0e' }}>{t('fs_recalc_weights', 'Pesos por dimensión')}</Typography>
          {Object.entries(DIMENSION_META).map(([key, meta]) => (
            <Box key={key} sx={{ display: 'grid', gridTemplateColumns: '7vw 1fr 4vw', gap: '.7vw', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '.68vw', opacity: .9 }}>{t(meta.labelKey, shortKey(key))}</Typography>
              <Slider min={0} max={50} step={1} disabled={paramsDisabled} sx={scoreSliderSx} value={Number(draft?.weights?.[key] || 0)} onChange={(_event, value) => setWeight(key, value)} />
              <TextField size="small" type="number" disabled={paramsDisabled} value={Number(draft?.weights?.[key] || 0)} onChange={(event) => setWeight(key, event.target.value)} />
            </Box>
          ))}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 6vw 6vw', gap: '.8vw', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '.5vw' }}>
              <Switch disabled={paramsDisabled} sx={scoreSwitchSx} checked={draft?.allowOverscore !== false} onChange={(event) => setDraft((prev) => ({ ...prev, allowOverscore: event.target.checked }))} />
              <Typography sx={{ fontSize: '.72vw' }}>{t('fs_recalc_allow_over_100', 'Permitir scores > 100')}</Typography>
            </Box>
            <TextField size="small" type="number" disabled={paramsDisabled} label={t('fs_recalc_cap', 'Límite')} value={draft?.sfrCap ?? 15} onChange={(event) => setDraft((prev) => ({ ...prev, sfrCap: Number(event.target.value) }))} />
            <TextField size="small" type="number" disabled={paramsDisabled} label={t('fs_recalc_per_pct', 'Peso SFR')} value={draft?.sfrPerPct ?? 0.2} onChange={(event) => setDraft((prev) => ({ ...prev, sfrPerPct: Number(event.target.value) }))} />
          </Box>
          <TextField select size="small" disabled={!canAggregate} label={t('fs_recalc_aggregation', 'Agregación')} value={draft?.aggregation || 'food_weighted'} onChange={(event) => setDraft((prev) => ({ ...prev, aggregation: event.target.value }))} helperText={canAggregate ? undefined : t('fs_recalc_agg_only_grouped', 'Solo aplica al agregar (semana/mes o región exacta).')}>
            <MenuItem value="food_weighted">{t('fs_recalc_agg_food', 'Ponderada por alimento')}</MenuItem>
            <MenuItem value="mean">{t('fs_recalc_agg_mean', 'Promedio simple')}</MenuItem>
          </TextField>
          {canExact && draft?.mode === 'standard' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '.5vw' }}>
              <Switch sx={scoreSwitchSx} checked={Boolean(draft?.exact)} onChange={(event) => setDraft((prev) => ({ ...prev, exact: event.target.checked }))} />
              <Typography sx={{ fontSize: '.72vw' }}>{t('fs_recalc_exact', 'Cálculo exacto por centro')}</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '.6vw', px: '1.1vw', py: '1vh', borderTop: `1px solid ${dividerColor}` }}>
          <Button size="small" sx={scoreButtonSx} onClick={() => { onReset(); setDraft(level === 'centro' ? { ...defaults, mode: 'standard' } : defaults); }}>{t('fs_recalc_reset', 'Restablecer')}</Button>
          <Button size="small" sx={scoreButtonSx} onClick={onOriginal}>{t('fs_recalc_original', 'Volver al original')}</Button>
          <Button size="small" sx={scoreButtonSx} onClick={onClose}>{t('fs_recalc_cancel', 'Cancelar')}</Button>
          <Button size="small" variant="contained" sx={scoreButtonSx} onClick={() => onApply(draft)}>{t('fs_recalc_apply', 'Aplicar')}</Button>
        </Box>
      </Box>
    </Modal>
  );
};

const MetricCell = ({ mode, value, selected, onClick }) => {
  const score = value?.score;
  const attention = attentionPctForCell(value);
  const empty = mode === 'attention' ? attention == null : score == null || value?.status === 'fasted';
  const text = mode === 'attention'
    ? (attention == null ? '-' : `${Number(attention).toFixed(0)}%`)
    : (score == null ? '-' : Number(score).toFixed(0));
  const color = mode === 'attention' ? attentionTextColor(attention) : scoreTextColor(score);
  const bg = mode === 'attention' ? attentionCellBg(attention) : scoreCellBg(score);
  const maxed = mode !== 'attention' && isMaxScore(score);

  return (
    <Tooltip title={value ? <TooltipContent data={value} mode={mode} /> : ''} placement="top">
      <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          width: '3.2vw',
          minWidth: '2.8rem',
          height: '2.8vh',
          border: maxed ? '0.14vh solid rgba(232,255,226,.95)' : selected ? '0.14vh solid #e84e0e' : '0.08vh solid transparent',
          borderRadius: '.45vh',
          background: empty ? 'transparent' : bg,
          color: empty ? 'rgba(166,166,166,.48)' : color,
          cursor: 'pointer',
          fontSize: '.72vw',
          fontWeight: empty ? 500 : 800,
          fontVariantNumeric: 'tabular-nums',
          '&:hover': { borderColor: '#e84e0e' },
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
};

const RankingRow = ({ index, mode, rankingBy, label, value, selected, onClick }) => {
  const metric = mode === 'attention'
    ? (rankingBy === 'reaction' ? formatSeconds(value.reaction_time?.mean_sec) : `${formatNumber(value.attentionPct)}%`)
    : Number(value.score).toFixed(1);
  const color = mode === 'attention' && rankingBy !== 'reaction' ? attentionTextColor(value.attentionPct) : scoreTextColor(value.score);
  return (
    <Box onClick={onClick} sx={{ py: '.55vh', px: '.45vw', borderRadius: '.4vh', cursor: 'pointer', background: selected ? 'rgba(232,78,14,.12)' : 'transparent', '&:hover': { background: 'rgba(255,255,255,.05)' } }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '.45vw', fontSize: '.74vw' }}>
        <span style={{ color: 'rgba(255,255,255,.5)', minWidth: '1.3vw', fontVariantNumeric: 'tabular-nums' }}>{index + 1}</span>
        <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        <strong style={{ color, fontVariantNumeric: 'tabular-nums' }}>{metric}</strong>
      </Box>
      {mode !== 'attention' && <ScoreBar value={value.score} />}
    </Box>
  );
};

const ScoreBar = ({ value }) => (
  <Box sx={{ height: '.45vh', borderRadius: '.45vh', background: 'rgba(255,255,255,.1)', overflow: 'hidden', mt: '.5vh' }}>
    <Box sx={{ height: '100%', width: `${Math.max(0, Math.min(100, value || 0))}%`, background: scoreTextColor(value), borderRadius: '.45vh' }} />
  </Box>
);

// El score comparte el gesto del icono de detalle: si la celda tiene desglose, el
// numero es tambien el disparador del modal.
const ScoreValue = ({ score, color, onOpenDetail, title }) => {
  const text = score == null ? '-' : Number(score).toFixed(1);
  const value = (
    <Typography
      component={onOpenDetail ? 'button' : 'span'}
      type={onOpenDetail ? 'button' : undefined}
      onClick={onOpenDetail || undefined}
      sx={{
        display: 'block',
        fontSize: '1.05vw',
        fontWeight: 800,
        color,
        fontVariantNumeric: 'tabular-nums',
        ...(onOpenDetail ? {
          border: 'none',
          background: 'none',
          p: 0,
          font: 'inherit',
          fontSize: '1.05vw',
          fontWeight: 800,
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' },
        } : {}),
      }}
    >
      {text}
    </Typography>
  );
  return onOpenDetail ? <Tooltip title={title}>{value}</Tooltip> : value;
};

const DetailPanel = ({ data, mode, t, onOpenDetail, isCenterLevel }) => {
  if (!data) {
    return <Typography sx={{ fontSize: '.75vw', opacity: .58 }}>{t('fs_select_cell', 'Selecciona una celda para ver detalles.')}</Typography>;
  }
  const attention = attentionPctForCell(data);
  // Al agregar por semana o mes, `cycles` proviene del ultimo dia del bucket: el
  // desglose no representaria las cifras agregadas que muestra el panel.
  const hasAudit = isCenterLevel && !data.agg && Array.isArray(data.cycles) && data.cycles.length > 0;
  const detailTitle = t('fs_detail_info_button', 'Información detallada');
  return (
    <Box sx={{ display: 'grid', gap: '.55vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.5vw' }}>
        <Typography sx={{ fontSize: '.9vw', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {data.name || data.scope || t('detail', 'Detalle')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '.15vw', flex: 'none' }}>
          {hasAudit && (
            <Tooltip title={detailTitle}>
              <IconButton size="small" onClick={onOpenDetail} sx={{ color: SCORE_ACCENT, p: '.25vh' }}>
                <ManageSearchIcon sx={{ fontSize: '1vw' }} />
              </IconButton>
            </Tooltip>
          )}
          <ScoreValue
            score={data.score}
            color={scoreTextColor(data.score)}
            onOpenDetail={hasAudit ? onOpenDetail : null}
            title={detailTitle}
          />
        </Box>
      </Box>
      <MetricLine label={t('delivered_food', 'Alimento entregado')} value={`${formatNumber(data.delivered_food_kg)} kg`} />
      <MetricLine label={t('fs_header_attended_alarms', 'Alarmas atendidas')} value={attention == null ? '-' : `${formatNumber(attention)}%`} color={attentionTextColor(attention)} />
      <MetricLine label={t('fs_tt_alarms', 'Alarmas (no atend.)')} value={`${data.alarmas_total || 0} (${data.alarmas_no_atendidas || 0})`} />
      <MetricLine label={t('fs_header_reaction', 'Reacción a alarmas')} value={data.reaction_time?.mean_sec == null ? '-' : formatSeconds(data.reaction_time.mean_sec)} />
      {/* Q viene en [0,1]; se expresa como porcentaje y con el mismo vocabulario que la
          linea de composicion del modal, donde Calidad = (100 - base) * Q. */}
      {mode !== 'attention' && data.Q != null && (
        <MetricLine label={t('fs_detail_quality', 'Calidad')} value={`${formatNumber(Number(data.Q) * 100, 1)}%`} />
      )}
    </Box>
  );
};

const MetricLine = ({ label, value, color }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '1vw', fontSize: '.72vw' }}>
    <span style={{ opacity: .65 }}>{label}</span>
    <strong style={{ color: color || '#f3f5f8', fontVariantNumeric: 'tabular-nums' }}>{value}</strong>
  </Box>
);

const TooltipContent = ({ data, mode }) => (
  <Box sx={{ bgcolor: '#101723', border: '1px solid rgba(232,78,14,.3)', borderRadius: '.5rem', p: '.7rem', minWidth: '11rem', color: '#f3f5f8', boxShadow: '0 8px 28px rgba(0,0,0,.45)' }}>
    <MetricLine label="Score" value={data.score == null ? '-' : Number(data.score).toFixed(1)} color={scoreTextColor(data.score)} />
    {data.recalc_exploratory && <MetricLine label="Modo" value={data.recalc_warning === 'mixed_rules' ? 'Reglas mixtas' : 'Exploratorio'} color="#e84e0e" />}
    {data.recalc_warning === 'not_recomputable' && <MetricLine label="Recalculo" value="No disponible" color="#e0a93b" />}
    {mode === 'attention' && <MetricLine label="Atención" value={attentionPctForCell(data) == null ? '-' : `${formatNumber(attentionPctForCell(data))}%`} color={attentionTextColor(attentionPctForCell(data))} />}
    <MetricLine label="Alimento" value={`${formatNumber(data.delivered_food_kg)} kg`} />
    <MetricLine label="Alarmas" value={`${data.alarmas_total || 0} (${data.alarmas_no_atendidas || 0})`} />
    <MetricLine label="Reacción" value={data.reaction_time?.mean_sec == null ? '-' : formatSeconds(data.reaction_time.mean_sec)} />
  </Box>
);

export const ScoreDetailModal = ({ open, onClose, data, day, t, variant = 'classic' }) => {
  const cycles = Array.isArray(data?.cycles) ? data.cycles : [];
  const [cycleIndex, setCycleIndex] = React.useState(0);
  const cycle = cycles[Math.min(cycleIndex, Math.max(0, cycles.length - 1))] || {};
  // La composicion describe el DIA, igual que el puntaje grande del encabezado: si
  // tomara la Q del ciclo, el total de la suma no cuadraria con el puntaje que esta
  // al lado y cambiaria de granularidad en silencio al mover el selector de visita.
  // Lo propio de cada visita va en su linea, explicitamente rotulado.
  const base = Number(data?.base ?? 0);
  const dayQ = Number(data?.Q ?? 1);
  const dayQuality = Math.max(0, 100 - base) * (Number.isFinite(dayQ) ? dayQ : 1);
  const daySfrBonus = Number(data?.sfr_bonus ?? 0);
  const goalKg = data?.meta_dia_kg ?? data?.inputs_summary?.meta_dia_kg;
  // Calidad se expresa en puntos dentro de la suma (es una identidad aritmetica) y
  // en porcentaje entre parentesis, que es la unidad que usa el panel lateral.
  const dayQPct = Number.isFinite(dayQ) ? dayQ * 100 : null;
  const cycleQ = Number(cycle?.Q);
  const cycleFoodKg = cycle?.food_kg;
  const isV2 = variant === 'v2';

  React.useEffect(() => { if (open) setCycleIndex(0); }, [open, data]);

  return (
    <Dialog
      open={Boolean(open && cycles.length)}
      onClose={onClose}
      maxWidth={isV2 ? 'lg' : 'md'}
      fullWidth
      BackdropProps={isV2 ? {
        sx: {
          backgroundColor: 'rgba(1,7,15,.82)',
          backdropFilter: 'blur(5px)',
        },
      } : undefined}
      PaperProps={{
        sx: isV2 ? {
          bgcolor: '#071425',
          backgroundImage: 'radial-gradient(circle at 88% 0%,rgba(46,179,238,.13),transparent 32%),linear-gradient(180deg,#0d2036 0%,#071425 100%)',
          color: '#e4e9f0',
          border: '1px solid rgba(46,179,238,.30)',
          borderRadius: '1rem',
          boxShadow: '0 2rem 6rem rgba(0,0,0,.72),inset 0 1px 0 rgba(255,255,255,.04)',
          width: 'min(64vw,72rem)',
          maxWidth: 'calc(100vw - 2rem)',
          fontFamily: "'IBM Plex Sans',sans-serif",
          overflow: 'hidden',
        } : {
          bgcolor: '#101723',
          color: '#f3f5f8',
        },
      }}
    >
      <DialogTitle sx={isV2 ? {
        p: '1rem 1.35rem',
        borderBottom: '1px solid rgba(46,179,238,.18)',
        background: 'rgba(3,10,22,.38)',
      } : undefined}>
        {isV2 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            <Box>
              <Typography sx={{ color: '#2eb3ee', fontSize: '.66rem', fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>
                Feeder Score · Auditoría
              </Typography>
              <Typography sx={{ mt: '.12rem', color: '#e4e9f0', fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 }}>
                {t('fs_detail_modal_title', 'Información detallada')}
              </Typography>
            </Box>
            <IconButton
              aria-label={t('close', 'Cerrar')}
              onClick={onClose}
              sx={{
                width: '2rem',
                height: '2rem',
                border: '1px solid rgba(46,179,238,.18)',
                borderRadius: '.55rem',
                color: '#8793a6',
                '&:hover': { color: '#e4e9f0', background: 'rgba(46,179,238,.10)' },
              }}
            >
              <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>×</span>
            </IconButton>
          </Box>
        ) : t('fs_detail_modal_title', 'Información detallada')}
      </DialogTitle>
      <DialogContent dividers sx={isV2 ? {
        borderColor: 'rgba(46,179,238,.18)',
        p: '1.15rem 1.35rem 1.35rem',
        background: 'linear-gradient(180deg,rgba(6,14,28,.20),rgba(3,10,22,.42))',
      } : { borderColor: dividerColor }}>
        <Box sx={{ display: 'grid', gap: isV2 ? '.8rem' : '1vh' }}>
          <Box sx={isV2 ? {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            p: '1rem 1.05rem',
            border: '1px solid rgba(46,179,238,.18)',
            borderRadius: '.75rem',
            background: 'linear-gradient(100deg,rgba(16,32,54,.82),rgba(8,18,34,.62))',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,.025)',
          } : { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1vw' }}>
            <Box>
              <Typography sx={isV2 ? { fontSize: '1rem', fontWeight: 700, color: '#e4e9f0' } : { fontSize: '.85rem', fontWeight: 800 }}>
                {data?.name || data?.scope || t('fs_cage', 'Jaula')}
              </Typography>
              <Typography sx={isV2 ? { mt: '.18rem', fontSize: '.72rem', color: '#8793a6' } : { fontSize: '.72rem', opacity: .58 }}>
                {day ? `${day} · ` : ''}{cycles.length} {cycles.length === 1 ? t('fs_visit_lower', 'visita') : t('fs_visits', 'visitas')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isV2 ? '1rem' : '.8rem' }}>
              {cycles.length > 1 && (
                <ButtonGroup size="small" sx={scoreButtonGroupSx}>
                  {cycles.map((item, index) => (
                    <Button
                      key={item.cycle_id || index}
                      variant={cycleIndex === index ? 'contained' : 'outlined'}
                      onClick={() => setCycleIndex(index)}
                    >
                      {index + 1}
                    </Button>
                  ))}
                </ButtonGroup>
              )}
              <Box sx={{ display: 'grid', justifyItems: 'end', gap: '.15rem' }}>
                {isV2 && (
                  <Typography sx={{ fontSize: '.62rem', color: '#8793a6', letterSpacing: '.08em', textTransform: 'uppercase' }}>
                    Score del día
                  </Typography>
                )}
                <Typography sx={{ fontSize: isV2 ? '2rem' : '1.6rem', lineHeight: 1, fontWeight: 800, color: scoreTextColor(data?.score), fontVariantNumeric: 'tabular-nums' }}>
                  {data?.score == null ? '-' : Number(data.score).toFixed(1)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={isV2 ? {
            display: 'flex',
            alignItems: 'center',
            gap: '.45rem',
            flexWrap: 'wrap',
            p: '.72rem .9rem',
            fontSize: '.7rem',
            color: '#8793a6',
            border: '1px solid rgba(46,179,238,.12)',
            borderRadius: '.65rem',
            background: 'rgba(6,14,28,.58)',
          } : { display: 'flex', gap: '.35rem', flexWrap: 'wrap', fontSize: '.7rem', opacity: .7 }}>
            <span>{t('fs_detail_composition', 'Composición')}:</span>
            <strong style={{ color: '#f3f5f8' }}>{t('fs_detail_base', 'Base')} {formatNumber(base)}</strong>
            <span>+</span>
            <strong style={{ color: '#f3f5f8' }}>
              {t('fs_detail_quality', 'Calidad')} {formatNumber(dayQuality)}
              {dayQPct == null ? '' : ` (${formatNumber(dayQPct, 1)}%)`}
            </strong>
            <span>+</span>
            <strong style={{ color: '#f3f5f8' }}>{t('fs_tt_sfr_bonus', 'Bono SFR')} {formatNumber(daySfrBonus)}</strong>
            <span>=</span>
            <strong style={{ color: '#f3f5f8' }}>{data?.score == null ? '-' : formatNumber(data.score)}</strong>
          </Box>
          <Box sx={isV2 ? {
            display: 'flex',
            gap: '1.25rem',
            flexWrap: 'wrap',
            p: '.72rem .9rem',
            fontSize: '.7rem',
            color: '#8793a6',
            border: '1px solid rgba(46,179,238,.12)',
            borderRadius: '.65rem',
            background: 'rgba(6,14,28,.42)',
          } : { display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '.7rem', opacity: .7 }}>
            <span>{t('fs_tt_goal_kg', 'Meta diaria')}: <strong style={{ color: '#f3f5f8' }}>{goalKg == null ? '-' : `${formatNumber(goalKg)} kg`}</strong></span>
            <span>{t('fs_tt_food', 'Comida entregada')}: <strong style={{ color: '#f3f5f8' }}>{formatNumber(data?.delivered_food_kg || 0)} kg</strong>{data?.porc_entregado != null ? ` (${formatNumber(data.porc_entregado)}%)` : ''}</span>
            <span>{t('fs_header_reaction', 'Reacción a alarmas')}: <strong style={{ color: '#f3f5f8' }}>{reactionHeaderText(data, t)}</strong></span>
          </Box>
          {cycles.length > 0 && (
            <Box sx={isV2 ? {
              display: 'flex',
              alignItems: 'center',
              gap: '1.1rem',
              flexWrap: 'wrap',
              p: '.72rem .9rem',
              fontSize: '.7rem',
              color: '#b8c1ce',
              border: '1px solid rgba(232,78,14,.24)',
              borderRadius: '.65rem',
              background: 'linear-gradient(90deg,rgba(232,78,14,.10),rgba(6,14,28,.40))',
            } : { display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '.7rem', opacity: .7 }}>
              <span style={{ color: SCORE_ACCENT, fontWeight: 700 }}>
                {t('fs_visit', 'Visita')} {cycleIndex + 1}:
              </span>
              <span>{t('fs_header_score', 'Score')}: <strong style={{ color: '#f3f5f8' }}>{cycle?.score == null ? '-' : formatNumber(cycle.score)}</strong></span>
              <span>{t('fs_detail_quality', 'Calidad')}: <strong style={{ color: '#f3f5f8' }}>{Number.isFinite(cycleQ) ? `${formatNumber(cycleQ * 100, 1)}%` : '-'}</strong></span>
              <span>{t('fs_tt_food', 'Comida entregada')}: <strong style={{ color: '#f3f5f8' }}>{cycleFoodKg == null ? '-' : `${formatNumber(cycleFoodKg)} kg`}</strong></span>
            </Box>
          )}

          <Grid container spacing={isV2 ? 1.25 : 1}>
            {buildDimensionAuditRows(cycle, data?.base).map((row) => (
              <Grid item xs={12} md={6} key={row.key}>
                <DimensionAuditCard row={row} cycle={cycle} t={t} variant={variant} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const DimensionAuditCard = ({ row, cycle, t, variant = 'classic' }) => {
  const { key, dim, pointsLost, q, weight } = row;
  const losesPoints = pointsLost > 0.005;
  const isV2 = variant === 'v2';
  return (
    <Box sx={{
      border: isV2 ? '1px solid rgba(46,179,238,.15)' : '1px solid ' + dividerColor,
      borderLeft: losesPoints ? (isV2 ? '.2rem solid #ff625f' : '.18rem solid #ff5252') : (isV2 ? '1px solid rgba(46,179,238,.15)' : '1px solid ' + dividerColor),
      borderRadius: isV2 ? '.75rem' : '.5rem',
      p: isV2 ? '.9rem 1rem' : '.75rem',
      background: isV2
        ? (losesPoints
          ? 'linear-gradient(110deg,rgba(255,82,82,.085),rgba(16,32,54,.58) 42%,rgba(8,18,34,.74))'
          : 'linear-gradient(110deg,rgba(46,179,238,.045),rgba(16,32,54,.58) 42%,rgba(8,18,34,.74))')
        : 'rgba(255,255,255,.035)',
      boxShadow: isV2 ? 'inset 0 1px 0 rgba(255,255,255,.025)' : 'none',
      minHeight: '100%',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '.8rem', alignItems: 'flex-start' }}>
        <Typography sx={{
          fontSize: isV2 ? '.76rem' : '.78rem',
          fontWeight: 800,
          color: isV2 ? '#e4e9f0' : 'inherit',
          letterSpacing: isV2 ? '.01em' : 'normal',
        }}>{dimensionLabel(key, t)}</Typography>
        <Box sx={{ display: 'flex', gap: '.25rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <DetailChip>{formatTemplate(t('fs_detail_weight_chip', 'peso {value}%'), { value: formatNumber(weight) })}</DetailChip>
          <DetailChip>{formatTemplate(t('fs_detail_q_chip', 'logro {value}%'), { value: formatNumber(q * 100) })}</DetailChip>
          <DetailChip good={!losesPoints} bad={losesPoints}>
            {losesPoints ? '-' + formatNumber(pointsLost) + ' pts' : t('fs_detail_no_discount', 'sin descuento')}
          </DetailChip>
        </Box>
      </Box>
      <Typography sx={{
        fontSize: isV2 ? '.7rem' : '.72rem',
        lineHeight: 1.48,
        mt: isV2 ? '.55rem' : '.45rem',
        color: losesPoints ? '#ffc4c4' : (isV2 ? '#b8c1ce' : '#f3f5f8'),
        opacity: losesPoints ? 1 : (isV2 ? .88 : .78),
      }}>
        {reasonText(key, dim, t)}
      </Typography>
      {key === 'stop' && <StopCriteriaInline cycle={cycle} t={t} />}
      {(key === 'alarm' || key === 'activity') && <EventList events={dim?.eventos || []} t={t} />}
      {key === 'depth_move' && dim?.reason?.params?.evaluable != null && (
        <Typography sx={{ fontSize: '.68rem', opacity: .62, mt: '.45rem' }}>
          {formatTemplate(t('fs_detail_ladder_sub', 'Peldaños alcanzados: {reached}/{evaluable}'), dim.reason.params)}
        </Typography>
      )}
    </Box>
  );
};

const DetailChip = ({ children, good, bad }) => (
  <Box component="span" sx={{
    fontSize: '.63rem',
    fontWeight: 800,
    px: '.45rem',
    py: '.12rem',
    borderRadius: '999px',
    whiteSpace: 'nowrap',
    color: bad ? '#ff5252' : good ? '#43c47a' : 'rgba(255,255,255,.66)',
    background: bad ? 'rgba(255,82,82,.13)' : good ? 'rgba(67,196,122,.14)' : 'rgba(138,138,138,.12)',
    fontVariantNumeric: 'tabular-nums',
  }}>
    {children}
  </Box>
);

const DOT_VARIANTS = {
  good: { background: '#43c47a', borderColor: '#43c47a' },
  bad: { background: '#ff5252', borderColor: '#ff5252' },
  neutral: { background: 'transparent', borderColor: 'rgba(255,255,255,.32)' },
};

// El estado se transmite por color, no por texto: el detalle textual queda en el
// title para no repetir una etiqueta por fila.
const StatusDot = ({ variant = 'neutral', title }) => {
  const dot = (
    <Box component="span" sx={{
      display: 'block',
      width: '.42rem',
      height: '.42rem',
      borderRadius: '50%',
      border: '1px solid',
      boxSizing: 'border-box',
      ...DOT_VARIANTS[variant],
    }} />
  );
  if (!title) return dot;
  return (
    <Tooltip title={title} placement="left">
      <Box component="span" sx={{ display: 'inline-flex', flex: 'none', cursor: 'default' }}>{dot}</Box>
    </Tooltip>
  );
};

// Los criterios de detencion son disyuntivos: basta uno para que la detencion sea
// correcta. Por eso "no se dio la senal" es un estado neutro y no una falta; el rojo
// se reserva para stop_by_table, que es propiedad del ciclo y no de un criterio.
// A diferencia de SCAPP, la nube no recibe la configuracion del centro, asi que no
// puede distinguir un criterio desactivado de uno sin senal.
const StopCriteriaInline = ({ cycle, t }) => {
  const stop = cycle?.dims?.stop;
  const causes = new Set(stop?.stop_causes || []);
  const criteria = [
    [t('fs_stop_criterion_alarm', 'Alarmas'), t('fs_stop_criterion_alarm_desc', 'Alarma reciente antes del cierre.'), 'alarma'],
    [t('fs_stop_criterion_perdidos', 'Alza de perdidos'), t('fs_stop_criterion_perdidos_desc', 'Delta de perdidos reciente antes del cierre.'), 'perdidos'],
    [t('fs_stop_criterion_activity', 'Caída de actividad'), t('fs_stop_criterion_activity_desc', 'Caída brusca de actividad reciente.'), 'actividad'],
    [t('fs_stop_criterion_goal', 'Meta alcanzada'), t('fs_stop_criterion_goal_desc', 'Meta diaria alcanzada antes del cierre.'), 'meta'],
    [t('fs_stop_criterion_phase', 'Fase de detención'), t('fs_stop_criterion_phase_desc', 'La política por fases exige detener.'), 'fase'],
  ];
  return (
    <Box sx={{ mt: '.55rem', pt: '.55rem', borderTop: `1px solid ${dividerColor}`, display: 'grid', gap: '.38rem' }}>
      {criteria.map(([label, detail, key]) => {
        const applied = causes.has(key);
        const state = applied
          ? t('fs_stop_criterion_applied', 'aplicó')
          : t('fs_stop_criterion_available', 'disponible');
        return (
          <Tooltip key={key} title={`${state} · ${detail}`} placement="top-start">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '.4rem', cursor: 'default', width: 'fit-content' }}>
              <StatusDot variant={applied ? 'good' : 'neutral'} />
              <span style={{ fontSize: '.68rem', color: '#f3f5f8' }}>{label}</span>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

const eventState = (event, t) => {
  if (event.recuperacion_natural) return { variant: 'good', title: t('fs_event_natural_recovery', 'recuperación natural') };
  if (event.atendido === true) return { variant: 'good', title: t('fs_event_attended', 'atendida') };
  if (event.atendido === false) return { variant: 'bad', title: t('fs_event_unattended', 'no atendida') };
  return { variant: 'neutral', title: t('fs_event_unknown', 'indeterminada') };
};

const EventList = ({ events, t }) => {
  if (!events.length) return null;
  return (
    <Box sx={{ mt: '.55rem', pt: '.55rem', borderTop: `1px solid ${dividerColor}`, display: 'grid', gap: '.32rem' }}>
      {events.map((event, index) => {
        const { variant, title } = eventState(event, t);
        return (
          <Box key={`${event.t}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.68rem', opacity: .72 }}>
            <StatusDot variant={variant} title={title} />
            <span style={{ opacity: .58, minWidth: '2.6rem', fontVariantNumeric: 'tabular-nums' }}>{formatEventTime(event.t)}</span>
            <span style={{ flex: 1 }}>{event.detalle || event.type || t('fs_detail_event', 'evento')}</span>
          </Box>
        );
      })}
    </Box>
  );
};

const buildDimensionAuditRows = (cycle, base = 0) => Object.entries(cycle?.dims || {})
  .map(([key, dim]) => {
    const q = clamp(Number(dim?.q), 0, 1, dim?.applicable === false ? 1 : 0);
    const weight = Number(dim?.w ?? DIMENSION_META[key]?.defaultWeight ?? 0);
    const applicable = dim?.applicable !== false;
    const pointsLost = applicable ? Math.max(0, (100 - Number(base || 0)) * weight / 100 * (1 - q)) : 0;
    return { key, dim, q, weight, pointsLost, neutral: !applicable };
  })
  .sort((a, b) => Number(a.neutral) - Number(b.neutral) || b.pointsLost - a.pointsLost || a.key.localeCompare(b.key));

const clamp = (value, min, max, fallback) => (Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : fallback);

const DIMENSION_LABEL_FALLBACKS = {
  start: 'Arranque gradual',
  initial_depth: 'Profundidad inicial',
  stationary: 'Ritmo sostenido',
  alarm: 'Reacción a alarmas',
  depth_move: 'Cambio de profundidad escalonada',
  activity: 'Reacción a la actividad',
  oxygen: 'Oxígeno',
  current: 'Correntómetro',
  stop: 'Detención oportuna',
};

const dimensionLabel = (key, t) => {
  const meta = DIMENSION_META[key] || {};
  const fallback = DIMENSION_LABEL_FALLBACKS[key] || shortKey(key);
  return meta.labelKey ? t(meta.labelKey, fallback) : fallback;
};

const reasonText = (key, dim, t) => {
  if (typeof dim?.reason === 'string') return dim.reason;
  const code = dim?.reason?.code;
  const p = dim?.reason?.params || {};
  const templates = {
    START_OK: t('fs_reason_start_ok', 'Arranque progresivo correcto.'),
    START_DIRECT_HIGH: t('fs_reason_start_direct_high', 'Arrancó directo en tasa alta.'),
    START_LOW_NO_PROGRESS: t('fs_reason_start_low_no_progress', 'Arranque bajo sin progresión visible.'),
    DEPTH_INIT_OK: t('fs_reason_depth_init_ok', 'Inició a {depth} m, dentro de {expected}±{tol} m.'),
    DEPTH_INIT_OUT: t('fs_reason_depth_init_out', 'Inició a {depth} m, fuera de {expected}±{tol} m.'),
    DEPTH_INIT_NO_DATA: t('fs_reason_depth_init_no_data', 'Sin profundidad suficiente en la ventana inicial.'),
    STAT_RATIO: t('fs_reason_stat_ratio', '{pct}% del tramo sostenido se mantuvo en tasa media/alta.'),
    ALARM_NO_EVENTS: t('fs_reason_alarm_no_events', 'No hubo alarmas ni alzas de perdidos que atender en la visita.'),
    ALARM_ATTENDED_RATIO: t('fs_reason_alarm_ratio', 'Atendió {attended} de {total} alarmas en la ventana.'),
    ALARM_NOT_EVALUABLE: t('fs_reason_alarm_not_evaluable', 'Alarmas sin profundidad suficiente para evaluar la reacción.'),
    LADDER_REACHED: t('fs_reason_ladder_reached', 'Cumplió {reached} de {evaluable} peldaños evaluables.'),
    LADDER_LOW_COVERAGE: t('fs_reason_ladder_low_coverage', 'Sin profundidad suficiente para evaluar la escalera ({coverage}%).'),
    LADDER_NO_CONFIG: t('fs_reason_ladder_no_config', 'Sin escalera de profundidad configurada.'),
    ACT_NO_EVENTS: t('fs_reason_activity_no_events', 'No hubo caídas bruscas de actividad que atender en la visita.'),
    ACT_ATTENDED_RATIO: t('fs_reason_activity_ratio', 'Reaccionó a {attended} de {total} caídas de actividad.'),
    ACT_ALL_NATURAL: t('fs_reason_activity_all_natural', 'Todas las caídas ({natural}) se recuperaron solas dentro de la ventana.'),
    OXY_BELOW_THRESHOLD: t('fs_reason_oxy_below', 'Alimentó con O2 bajo el umbral el {pct_below}% de la visita.'),
    CUR_ABOVE_THRESHOLD: t('fs_reason_cur_above', 'Alimentó con corriente sobre el umbral el {pct_above}% de la visita.'),
    SENSOR_NO_THRESHOLD: t('fs_reason_sensor_no_threshold', 'Sin umbral configurado para este sensor.'),
    SENSOR_NO_DATA: t('fs_reason_sensor_no_data', 'Sin lectura disponible para este sensor.'),
    SENSOR_LOW_COVERAGE: t('fs_reason_sensor_low_coverage', 'Cobertura del sensor insuficiente ({coverage}%).'),
    // stopCauseText ya aporta la preposicion ("por alarmas"), la plantilla no la repite.
    STOP_JUSTIFIED: t('fs_reason_stop_justified', 'Detención correcta {causes}.'),
    STOP_BY_TABLE: t('fs_reason_stop_by_table', 'Cortó al {pct}% de la meta sin señales de saciedad (por tabla).'),
    STOP_NO_STOP_AFTER_PHASE: t('fs_reason_stop_no_stop_after_phase', 'No detuvo pese a que la política de fases lo exigía.'),
    STOP_IN_PROGRESS: t('fs_reason_stop_in_progress', 'Visita en curso, aún no evaluable.'),
  };
  if (code === 'DIM_NEUTRAL') return neutralReasonText(p.motivo, t);
  if (code === 'STOP_JUSTIFIED') return formatTemplate(templates[code], { causes: (p.causes || []).map((cause) => stopCauseText(cause, t)).join(', ') || '-' });
  if (templates[code]) return formatTemplate(templates[code], p);
  return dim?.detalle || dim?.reason?.detalle || code || t('fs_reason_generic', 'Sin detalle disponible.');
};

const neutralReasonText = (motivo, t) => ({
  profundidad_desactivada: t('fs_neutral_depth_disabled', 'Profundidad desactivada.'),
  sin_alimentacion_activa: t('fs_neutral_no_active_feeding', 'Sin alimentación activa.'),
  sin_tasa_activa: t('fs_neutral_no_active_rate', 'Sin tasa activa.'),
  sin_tramo_estacionario: t('fs_neutral_no_stationary', 'Visita sin tramo sostenido.'),
  sin_disparadores_escalera: t('fs_neutral_no_ladder_triggers', 'Sin disparadores de escalera.'),
  oxigeno_desactivado: t('fs_neutral_oxygen_disabled', 'Oxígeno desactivado.'),
  correntometro_desactivado: t('fs_neutral_current_disabled', 'Correntómetro desactivado.'),
  sin_detencion_evaluable: t('fs_neutral_no_stop', 'Sin detención evaluable.'),
  sin_cierre_observado: t('fs_neutral_no_close', 'Sin cierre observado.'),
}[motivo] || t('fs_neutral', 'neutro'));

const stopCauseText = (cause, t) => ({
  alarma: t('fs_stop_cause_alarm', 'por alarmas'),
  perdidos: t('fs_stop_cause_perdidos', 'por perdidos'),
  actividad: t('fs_stop_cause_activity', 'por caída de actividad'),
  fase: t('fs_stop_cause_phase', 'por fase de detención'),
  meta: t('fs_stop_cause_goal', 'por meta alcanzada'),
}[cause] || cause);

const formatTemplate = (template, values = {}) => Object.entries(values).reduce(
  (text, [key, value]) => text.replaceAll(`{${key}}`, value),
  template,
);

const formatEventTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

// reaction_time solo acumula latencias de alarmas atendidas cuya reaccion pudo
// cronometrarse, asi que un "-" plano no distinguia tres situaciones distintas:
// no hubo alarmas, hubo pero ninguna se atendio, o se atendieron sin latencia
// medible (p. ej. el movimiento de camara sin timestamp de llegada).
const reactionHeaderText = (data, t) => {
  const mean = data?.reaction_time?.mean_sec;
  if (mean != null) return formatSeconds(mean);
  const total = Number(data?.alarmas_total ?? data?.reasons_summary?.alarmas_total ?? 0);
  if (total <= 0) return t('fs_reaction_no_alarms', 'sin alarmas');
  const unattended = Number(data?.alarmas_no_atendidas ?? data?.reasons_summary?.alarmas_no_atendidas ?? 0);
  if (unattended >= total) return t('fs_reaction_none_attended', 'ninguna atendida');
  return t('fs_reaction_not_timed', 'sin latencia medible');
};

const formatSeconds = (seconds) => {
  const value = Number(seconds);
  if (!Number.isFinite(value)) return '-';
  if (value < 60) return `${Math.round(value)} s`;
  return `${Math.floor(value / 60)}m ${Math.round(value % 60)}s`;
};

const formatScoreRange = (min, max) => {
  if (min == null || max == null) return '-';
  return `${Number(min).toFixed(1)}-${Number(max).toFixed(1)}`;
};

// Acento naranjo de la plataforma (los overrides globales del theme no aplican
// en ServiceManager, así que se estiliza localmente como en el resto de Scaap).
const SCORE_ACCENT = '#e84e0e';

// Botones (Recalcular, footer del modal, ranking, ciclos): reemplaza
// el azul "material puro" por el naranjo, cubriendo variantes outlined/contained/text.
const scoreButtonSx = {
  color: SCORE_ACCENT,
  boxShadow: 'none',
  '&:hover': { backgroundColor: 'rgba(232,78,14,.10)', boxShadow: 'none' },
  '&.MuiButton-outlined': {
    borderColor: 'rgba(232,78,14,.45)',
    '&:hover': { borderColor: SCORE_ACCENT, backgroundColor: 'rgba(232,78,14,.10)' },
  },
  '&.MuiButton-contained': {
    color: '#ffb495',
    backgroundColor: 'rgba(232,78,14,.20)',
    border: '1px solid rgba(232,78,14,.55)',
    '&:hover': { backgroundColor: 'rgba(232,78,14,.30)' },
  },
  '&.Mui-disabled': {
    color: 'rgba(166,166,166,.4)',
    borderColor: 'rgba(166,166,166,.2)',
    backgroundColor: 'transparent',
  },
};

// Aplica el estilo naranjo a todos los botones del grupo y tiñe el separador interno.
const scoreButtonGroupSx = {
  '& .MuiButtonGroup-grouped:not(:last-of-type)': {
    borderRightColor: 'rgba(232,78,14,.45)',
  },
  '& .MuiButton-root': scoreButtonSx,
};

// Sliders del modal de recálculo: track/thumb/rail en naranjo en vez del azul MUI.
const scoreSliderSx = {
  color: SCORE_ACCENT,
  '& .MuiSlider-rail': { opacity: .28 },
  '& .MuiSlider-thumb': {
    '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 .5vh rgba(232,78,14,.16)' },
    '&.Mui-active': { boxShadow: '0 0 0 .7vh rgba(232,78,14,.16)' },
  },
  '&.Mui-disabled': { color: 'rgba(232,78,14,.30)' },
};

// Switches del mismo modal: coherencia con los sliders (naranjo al estar activos).
const scoreSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: SCORE_ACCENT },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: SCORE_ACCENT },
};

const tableContainerSx = {
  height: '100%',
  maxHeight: '100%',
  background: 'rgba(255,255,255,.025)',
  border: `1px solid ${dividerColor}`,
  borderRadius: '.55rem',
  overflow: 'auto',
};

const sidePanelSx = {
  height: '100%',
  border: `1px solid ${dividerColor}`,
  borderRadius: '.55rem',
  p: '1vh .8vw',
  background: 'rgba(255,255,255,.025)',
  overflow: 'hidden',
};

const headerMetricSx = {
  minHeight: '7.3vh',
  border: `1px solid ${dividerColor}`,
  borderRadius: '.55rem',
  px: '1vw',
  py: '.9vh',
  background: 'rgba(255,255,255,.025)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};

const metricLabelSx = {
  width: 'fit-content',
  fontSize: '.62vw',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  color: '#e84e0e',
  fontWeight: 800,
  mb: '.3vh',
};

const cellHeadSx = {
  bgcolor: '#111a27',
  color: '#f3f5f8',
  borderBottom: `1px solid ${dividerColor}`,
  fontSize: '.72vw',
  fontWeight: 800,
};

const stickyColSx = {
  position: 'sticky',
  left: 0,
  zIndex: 3,
  minWidth: '9rem',
};

const nameCellSx = {
  ...stickyColSx,
  bgcolor: '#101723',
  color: '#f3f5f8',
  borderBottom: `1px solid ${dividerColor}`,
  fontSize: '.72vw',
  maxWidth: '13rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const cellSx = {
  borderBottom: `1px solid ${dividerColor}`,
  minWidth: '4rem',
  p: '.35rem',
};

const rowSx = {
  '&:hover td': { bgcolor: 'rgba(255,255,255,.035)' },
};

export default CloudFeederScorePanel;
